/**
 * 👽 CURIOUS ALIEN TEMPERAMENT
 * Observes from afar, interest builds to ABDUCTION MODE!
 * They come in peace... or do they?
 *
 * Created by Tom & Azura - January 14, 2026
 */

import type { Temperament } from '../types';

export const CuriousAlien: Temperament = {
  id: 'curious-alien',
  name: 'Curious Alien',
  emoji: '👽',
  description: 'Observing... analyzing... until FULL CONTACT! Beam me up!',

  // ============================================================
  // MOODS - Building toward CONTACT
  // ============================================================

  moods: ['observing', 'interested', 'analyzing', 'fascinated', 'contact', 'processing'],
  initialMood: 'observing',

  // ============================================================
  // MOOD TRANSITIONS
  // ============================================================

  moodMatrix: {
    observing: [
      { mood: 'observing', weight: 0.55 },
      { mood: 'interested', weight: 0.45 },
    ],
    interested: [
      { mood: 'observing', weight: 0.15 },
      { mood: 'interested', weight: 0.35 },
      { mood: 'analyzing', weight: 0.5 },
    ],
    analyzing: [
      { mood: 'interested', weight: 0.15 },
      { mood: 'analyzing', weight: 0.35 },
      { mood: 'fascinated', weight: 0.5 },
    ],
    fascinated: [
      { mood: 'analyzing', weight: 0.1 },
      { mood: 'fascinated', weight: 0.4 },
      { mood: 'contact', weight: 0.5 },
    ],
    contact: [
      { mood: 'contact', weight: 0.6 },
      { mood: 'processing', weight: 0.4 },
    ],
    processing: [
      { mood: 'processing', weight: 0.3 },
      { mood: 'observing', weight: 0.6 },
      { mood: 'interested', weight: 0.1 },
    ],
  },

  // ============================================================
  // MOOD DURATIONS
  // ============================================================

  moodDurations: {
    observing:   { base: 2800, variance: 3500 },
    interested:  { base: 500, variance: 700 },
    analyzing:   { base: 400, variance: 600 },
    fascinated:  { base: 300, variance: 400 },
    contact:     { base: 200, variance: 300 },
    processing:  { base: 1200, variance: 1800 },
  },

  moodConstraints: {
    contact: {
      minDuration: 2500,
      maxDuration: 5500,
    },
  },

  // ============================================================
  // ENERGY SYSTEM - Builds curiosity!
  // ============================================================

  energy: {
    max: 100,
    initial: 0,
    regenRate: 7,

    moodDrain: {
      observing:   -2,   // Slowly gathering data...
      interested:  -3,   // More intrigued...
      analyzing:   -2,   // Processing...
      fascinated:  -1,   // Must know more!
      contact:     28,   // Full beam power drain!
      processing:  -5,   // Data overload recovery
    },

    thresholds: {
      exhausted: 10,
      low: 25,
      high: 55,
      explosive: 72,
    },

    restingMoods: ['observing', 'processing', 'interested', 'analyzing'],
  },

  // ============================================================
  // EXPRESSIONS
  // ============================================================

  expressions: {
    observing: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 0.98,
      transitionMs: 400,
      jitter: 1,
      glowIntensity: 0.2,
      glowColor: 'rgba(100, 255, 150, 0.3)',
    },
    interested: {
      rotation: { x: -18, y: -25, z: 2 },
      translation: { x: 0, y: -1, z: 0 },
      scale: 1,
      transitionMs: 200,
      jitter: 2,
      glowIntensity: 0.3,
      glowColor: 'rgba(100, 255, 150, 0.5)',
    },
    analyzing: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 150,
      jitter: 4,
      glowIntensity: 0.5,
      glowColor: 'rgba(100, 200, 255, 0.6)',
    },
    fascinated: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -2, z: 0 },
      scale: 1.02,
      transitionMs: 100,
      jitter: 6,
      glowIntensity: 0.7,
      glowColor: 'rgba(150, 100, 255, 0.7)',
    },
    contact: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -3, z: 0 },
      scale: 1.05,
      transitionMs: 50,
      jitter: 8,
      glowIntensity: 1,
      glowColor: 'rgba(200, 150, 255, 1)',
      cssClass: 'contact',
    },
    processing: {
      rotation: { x: -22, y: -25, z: -2 },
      translation: { x: 0, y: 2, z: 0 },
      scale: 0.97,
      transitionMs: 400,
      jitter: 1,
      glowIntensity: 0.2,
      glowColor: 'rgba(100, 255, 150, 0.3)',
    },
  },

  // ============================================================
  // CORNER BOUNCING (for contact mode - hovering!)
  // ============================================================

  corners: {
    center: {
      transform: { x: 0, y: 0, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      bulge: { x: 50, y: 50 },
    },
    topLeft: {
      transform: { x: -2, y: 2, z: 2 },
      translation: { x: -4, y: -5, z: 0 },
      bulge: { x: 30, y: 30 },
    },
    topRight: {
      transform: { x: -2, y: -2, z: -2 },
      translation: { x: 4, y: -5, z: 0 },
      bulge: { x: 70, y: 30 },
    },
    bottomLeft: {
      transform: { x: 2, y: 2, z: -2 },
      translation: { x: -4, y: 3, z: 0 },
      bulge: { x: 30, y: 70 },
    },
    bottomRight: {
      transform: { x: 2, y: -2, z: 2 },
      translation: { x: 4, y: 3, z: 0 },
      bulge: { x: 70, y: 70 },
    },
  },

  bouncingMoods: ['contact', 'fascinated'],

  bounceIntervals: {
    contact: 150,
    fascinated: 300,
  },

  // ============================================================
  // STIMULI HANDLERS
  // ============================================================

  stimulusHandlers: {
    time: (_stimulus, currentMood, energy) => {
      if (currentMood === 'observing' && energy < 15) {
        return { energyDelta: 4 };
      }
      if (currentMood === 'observing' && energy >= 15) {
        return { transitionTo: 'interested', energyDelta: 3 };
      }
      if (currentMood === 'interested') {
        return { transitionTo: 'analyzing', energyDelta: 2 };
      }
      return null;
    },

    poke: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Interaction = MORE INTERESTING to the alien!
      if (currentMood === 'observing') {
        return { transitionTo: 'interested', energyDelta: 20 * intensity };
      }
      if (currentMood === 'interested') {
        return { transitionTo: 'analyzing', energyDelta: 20 * intensity };
      }
      if (currentMood === 'analyzing') {
        return { transitionTo: 'fascinated', energyDelta: 22 * intensity };
      }
      if (currentMood === 'fascinated') {
        return { transitionTo: 'contact', energyDelta: 28 * intensity, forceDuration: 2500 };
      }
      if (currentMood === 'processing') {
        return { transitionTo: 'interested', energyDelta: 15 * intensity };
      }
      return { energyDelta: 15 * intensity };
    },

    shake: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Shaking = FASCINATING specimen behavior!
      if (currentMood === 'observing' || currentMood === 'interested') {
        return { transitionTo: 'fascinated', energyDelta: 35 * intensity };
      }
      if (currentMood === 'analyzing' || currentMood === 'fascinated' || energy > 40) {
        return { transitionTo: 'contact', energyDelta: 42 * intensity, forceDuration: 3000 };
      }
      return { transitionTo: 'fascinated', energyDelta: 30 * intensity };
    },
  },

  // ============================================================
  // MOOD INDICATORS
  // ============================================================

  moodIndicators: {
    observing: {
      emoji: '🛸',
      text: '...',
      label: '*scanning from afar*',
    },
    interested: {
      emoji: '👀',
      text: '?',
      label: 'interesting...',
    },
    analyzing: {
      emoji: '🔬',
      text: '??',
      label: '*running analysis*',
    },
    fascinated: {
      emoji: '🤯',
      text: '?!',
      label: 'FASCINATING SPECIMEN',
    },
    contact: {
      emoji: '👽',
      text: '!!!',
      label: '🛸 INITIATING CONTACT 🛸',
    },
    processing: {
      emoji: '💫',
      text: '~~~',
      label: '*data overload*',
    },
  },
};
